import { useEffect, useMemo, useState, type ReactNode } from "react";
import { MessageBar, MessageBarBody } from "@fluentui/react-components";
import { useAuthState } from "../../../auth/AuthStateProvider";
import { useOptionalAppConfig } from "../../../services/studio/AppConfigProvider";
import { ModuleHeader } from "../../../shared/components/ModuleHeader";
import { CalendarIcon } from "../../../shared/components/icons";
import { logConnectorFailure, logConnectorSuccess } from "../../../telemetry";
import type { CalEvent, Self, WorkingHours } from "../types";
import { seriesChange, type CalendarChange } from "../domain/changes";
import { isActive } from "../domain/day";
import { busyDuring } from "../domain/freeBusy";
import { domainOf, isExternal } from "../domain/people";
import { dayKeyOf, dayLabel, fd, hm, minutesOfDay, rangeLabel } from "../domain/time";
import { useAvailability, type LoadSchedules } from "../hooks/useAvailability";
import { calendarScreenProps, useCalendarKeys } from "../hooks/useCalendarKeys";
import type { Repeat } from "../services/calendarWrites";
import type { Person } from "../services/people";
import {
  canOpenOutlookForm,
  createTeamsJoin,
  openOutlookForm,
  openOutlookWebCompose,
  TEAMS_LOCATION,
  TEAMS_TOGGLE_BANNER,
  TEAMS_TOGGLE_TEXT,
  type NewMeetingDraft,
} from "../services/newMeeting";
import { AttendeeInput, availabilityOf, type Availability } from "./AttendeeInput";
import { CheckIcon, CloseSmIcon, WarnIcon } from "./CalendarIcons";
import { TimeFinder } from "./TimeFinder";
import styles from "./CalendarModule.module.css";

const DURATIONS = [15, 30, 45, 60, 90] as const;

/** An attendee pill's tooltip when their availability at the chosen time is known. */
const PILL_TITLE = { free: "Free at the chosen time", busy: "Busy at the chosen time" } as const;

type Place = "teams" | "inperson";
const PLACES: readonly { readonly value: Place; readonly label: string }[] = [
  { value: "teams", label: "Teams" },
  { value: "inperson", label: "In person" },
];

const REPEATS: readonly { readonly value: Repeat; readonly label: string }[] = [
  { value: "none", label: "Does not repeat" },
  { value: "weekly", label: "Weekly" },
  { value: "fortnightly", label: "Fortnightly" },
];
const WEEKDAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"] as const;

/** Creating the invite: making the Teams link, then (when it couldn't open on its own) waiting for a click. */
type Creating =
  | { readonly state: "idle" }
  | { readonly state: "preparing" }
  | { readonly state: "ready"; readonly draft: NewMeetingDraft; readonly teamsAdded: boolean | null };

const OPENS_IN_OUTLOOK = "Opens in Outlook, filled in. Send it from there, or save it to keep it as a draft on your calendar.";
const SENDS_FROM_AIVA = "Outlook's form can't set a repeat, so AiVA sends this invite itself after a 5-second Undo.";

/** How long after a click a new window may still open: Edge and Chrome allow 5 seconds, so this leaves a margin. */
const WEB_OPEN_WINDOW_MS = 3000;

interface NewMeetingViewProps {
  /** The time clicked in the day view, when the meeting was started from one. */
  readonly preset: Date | null;
  /** 2 to open straight on the time finder ("Find a Time"). */
  readonly startStep: 1 | 2;
  readonly now: Date;
  /** The week on screen, for whether you're free at the preset time. */
  readonly events: readonly CalEvent[];
  readonly workingHours: WorkingHours;
  readonly token: string;
  readonly self: Self;
  readonly loadSchedules: LoadSchedules;
  readonly toast: ReactNode;
  readonly onClose: () => void;
  /**
   * The invite is open in Outlook (`web`: Outlook on the web, the last resort). `teamsAdded` is null when it
   * isn't a Teams meeting.
   */
  readonly onOpened: (info: { readonly teamsAdded: boolean | null; readonly start: Date; readonly web: boolean }) => void;
  readonly onError: (message: string) => void;
  /** A repeating meeting, sent by AiVA itself when its Undo window ends. */
  readonly onSend: (change: CalendarChange, start: Date) => void;
}

/**
 * New Meeting (section E): who and what, then when. Booking opens Outlook's
 * own meeting form, filled in: the user sends it from there, or saves it, and
 * Outlook keeps it as an unsent draft that shows on the calendar as "Draft ·
 * Send in Outlook". AiVA can't save that draft itself: Graph sends the invites
 * of any meeting it creates. A repeating meeting can't go through that form
 * (it can't be given a repeat), so AiVA sends it itself.
 */
export function NewMeetingView({
  preset: initialPreset,
  startStep,
  now,
  events,
  workingHours,
  token,
  self,
  loadSchedules,
  toast,
  onClose,
  onOpened,
  onError,
  onSend,
}: Readonly<NewMeetingViewProps>) {
  const { refreshGraphToken } = useAuthState();
  const [step, setStep] = useState<1 | 2>(startStep);
  const [attendees, setAttendees] = useState<readonly Person[]>([]);
  const [subject, setSubject] = useState("");
  const [duration, setDuration] = useState<number>(30);
  const [place, setPlace] = useState<Place>("teams");
  const [where, setWhere] = useState("");
  const [repeat, setRepeat] = useState<Repeat>("none");
  const [preset, setPreset] = useState<Date | null>(initialPreset);
  const [creating, setCreating] = useState<Creating>({ state: "idle" });

  // Esc goes back a step, as the header's Back button does.
  useCalendarKeys((e) => {
    if (e.key !== "Escape" || e.altKey || e.ctrlKey || e.metaKey) return;
    e.preventDefault();
    if (step === 2) setStep(1);
    else onClose();
  });

  const emails = useMemo(() => attendees.map((a) => a.email), [attendees]);
  const names = useMemo(() => new Map(attendees.map((a) => [a.email, a.name])), [attendees]);

  // Anyone outside the organisation is named before the invite goes — whether AiVA sends it (a repeating
  // meeting) or Outlook's form opens with it — unless the Studio "External recipient warning" is switched off.
  const { ownDomains, outsiders } = useOutsiders(attendees, self);
  const firstName = (email: string) => (names.get(email) ?? email).trim().split(/\s+/)[0] ?? email;

  // A prepared invite belongs to the form as it was: any change starts over.
  const formKey = `${subject}|${emails.join(",")}|${duration}|${place}|${where}|${repeat}|${preset?.getTime() ?? ""}`;
  useEffect(() => setCreating({ state: "idle" }), [formKey]);

  const presetRange = preset ? { start: preset, end: new Date(preset.getTime() + duration * 60000) } : null;
  const presetAvailability = useAvailability(
    loadSchedules,
    emails,
    presetRange?.start ?? new Date(0),
    presetRange?.end ?? new Date(0),
    presetRange !== null && emails.length > 0,
  );
  const statusAtPreset = (email: string): Availability => {
    if (!presetRange || !presetAvailability) return "unknown";
    return availabilityOf(busyDuring(presetAvailability, email, presetRange, false));
  };

  /** Open the invite in Outlook on the web; if it can't open, it waits for a click on Open in Outlook to try again. */
  const openOnWeb = (draft: NewMeetingDraft, teamsAdded: boolean | null) => {
    try {
      openOutlookWebCompose(draft);
    } catch (e: unknown) {
      setCreating({ state: "ready", draft, teamsAdded });
      logConnectorFailure("calendar", e, "open-new-meeting-web");
      onError("Outlook on the web couldn't open. Try again — nothing was sent.");
      return;
    }
    setCreating({ state: "idle" });
    logConnectorSuccess("calendar", "open-new-meeting-web");
    onOpened({ teamsAdded, start: draft.start, web: true });
  };

  /**
   * Make the Teams link if wanted, then open Outlook's form. Where Outlook has no form (Word), Outlook on the web
   * opens instead while the click is recent enough for a new window; a slow Teams link leaves it for a second click.
   */
  const create = async (start: Date, end: Date) => {
    if (creating.state === "preparing") return;
    const clickedAt = Date.now();
    setCreating({ state: "preparing" });
    let bodyHtml = "";
    let bodyText = "";
    let location = "";
    let teamsAdded: boolean | null = null;
    if (place === "teams") {
      location = TEAMS_LOCATION;
      try {
        const join = await createTeamsJoin(token, () => refreshGraphToken(false), subject.trim(), start, end);
        bodyHtml = join.html;
        bodyText = `Join the Microsoft Teams meeting: ${join.url}`;
        teamsAdded = true;
        logConnectorSuccess("calendar", "create-teams-link");
      } catch (e: unknown) {
        // The invite still opens; it says to turn Teams on in the form, as Chat's booking does.
        bodyHtml = TEAMS_TOGGLE_BANNER;
        bodyText = TEAMS_TOGGLE_TEXT;
        teamsAdded = false;
        logConnectorFailure("calendar", e, "create-teams-link");
      }
    } else {
      location = where.trim();
    }
    const draft: NewMeetingDraft = { subject: subject.trim(), attendees: emails, start, end, location, bodyHtml, bodyText };

    if (!canOpenOutlookForm()) {
      if (Date.now() - clickedAt <= WEB_OPEN_WINDOW_MS) {
        openOnWeb(draft, teamsAdded);
        return;
      }
      setCreating({ state: "ready", draft, teamsAdded });
      onError("The Teams link took a while. Click Open in Outlook to open the invite — nothing was sent.");
      return;
    }
    try {
      await openOutlookForm(draft);
      setCreating({ state: "idle" });
      logConnectorSuccess("calendar", "open-new-meeting-form");
      onOpened({ teamsAdded, start, web: false });
    } catch (e: unknown) {
      // The Outlook app couldn't open it: Outlook on the web is the last resort, a click away.
      setCreating({ state: "ready", draft, teamsAdded });
      logConnectorFailure("calendar", e, "open-new-meeting-form");
      onError("Outlook couldn't open its meeting form. Open it on the web instead — nothing was sent.");
    }
  };

  /** A repeating meeting: AiVA creates the series, and the invites go out when the Undo window ends. */
  const sendSeries = (start: Date, end: Date, every: Exclude<Repeat, "none">) => {
    const meeting = { subject: subject.trim(), attendees, start, end, location: where.trim(), teams: place === "teams", repeat: every };
    onSend(seriesChange(meeting, self, outsiders.length), start);
  };

  /** A booking button's label: send a repeating meeting, or open Outlook (then its web page if the form failed). */
  const actionLabel = (createLabel: string) => {
    const every = REPEATS.find((r) => r.value === repeat)?.label;
    if (repeat !== "none") return attendees.length > 0 ? `Send ${every} Invite` : `Add ${every} Meeting`;
    if (creating.state === "preparing") return place === "teams" ? "Adding the Teams link…" : "Opening…";
    return creating.state === "ready" ? "Open in Outlook" : createLabel;
  };
  const act = (start: Date, end: Date) => {
    if (repeat !== "none") sendSeries(start, end, repeat);
    else if (creating.state === "ready") openOnWeb(creating.draft, creating.teamsAdded);
    else void create(start, end);
  };
  const bookHint = repeat === "none" ? OPENS_IN_OUTLOOK : SENDS_FROM_AIVA;
  const externalWarning = <ExternalInviteWarning outsiders={outsiders} />;

  const backToStepOne = () => setStep(1);
  const header = (
    <ModuleHeader
      title="New Meeting"
      subtitle={`Step ${step} of 2 · ${step === 1 ? "who and what" : "when"}`}
      icon={CalendarIcon}
      accent="calendar"
      onBack={step === 2 ? backToStepOne : onClose}
      backLabel={step === 2 ? "Back to step 1" : "Back to calendar"}
    />
  );

  const placeLabel = place === "teams" ? "Teams" : where.trim() || "In person";

  if (step === 2) {
    return (
      <div className={styles.module} {...calendarScreenProps}>
        {header}
        <div className={styles.body}>
          <TimeFinder
            summary={
              <div className={`${styles.card} ${styles.newSummary}`}>
                <span className={styles.optBody}>
                  <span className={styles.optName}>{subject.trim() || "New meeting"}</span>
                  <span className={styles.optDesc}>
                    {fd(duration)} · {placeLabel} · {attendees.length} attendee{attendees.length === 1 ? "" : "s"}
                    {repeat !== "none" && ` · ${repeat}`}
                  </span>
                </span>
                <button type="button" className={styles.newLink} onClick={() => setStep(1)}>
                  Edit
                </button>
              </div>
            }
            people={emails}
            names={names}
            durationMin={duration}
            moving={null}
            now={now}
            workingHours={workingHours}
            token={token}
            loadSchedules={loadSchedules}
            selectedLine={repeat === "none" ? "Selected — open it in Outlook below" : "Selected — send it below"}
            pickedTitle="Ready to Create"
            pickedDetail={subject.trim() || "New meeting"}
            confirmLabel={actionLabel("Open in Outlook")}
            confirmDisabled={creating.state === "preparing"}
            pickedFooter={
              <>
                {externalWarning}
                <div className={`${styles.hint} ${styles.bandHint}`}>{bookHint}</div>
              </>
            }
            onConfirm={act}
            onPickChange={() => setCreating({ state: "idle" })}
          />
        </div>
        {toast}
      </div>
    );
  }

  // Step 1: who and what, and the clicked time if there is one.
  let when: ReactNode = (
    <button type="button" className={`${styles.btn} ${styles.btnLg}`} onClick={() => setStep(2)}>
      Find a Time
    </button>
  );
  if (presetRange) {
    when = (
      <PresetWhen
        start={presetRange.start}
        end={presetRange.end}
        events={events}
        emails={emails}
        statusAt={statusAtPreset}
        firstName={firstName}
        checking={presetAvailability === null}
        bookDisabled={creating.state === "preparing"}
        bookLabel={actionLabel}
        bookHint={bookHint}
        onBook={act}
        onChange={() => setPreset(null)}
        onFindBetter={() => setStep(2)}
      />
    );
  }

  return (
    <div className={styles.module} {...calendarScreenProps}>
      {header}
      <div className={styles.body}>
        <div>
          <div className={styles.fLbl}>
            Attendees<span className={styles.fLblRight}>{attendees.length} invited</span>
          </div>
          {attendees.length > 0 && (
            <div className={styles.pills}>
              {attendees.map((p) => {
                const s = statusAtPreset(p.email);
                return (
                  <span
                    key={p.email}
                    className={styles.pill}
                    title={s === "unknown" ? p.email : PILL_TITLE[s]}
                  >
                    {s !== "unknown" && <span className={`${styles.pillDot} ${s === "busy" ? styles.pillDotBusy : ""}`} />}
                    {p.name}
                    {/* The picker's External label, kept once the person is picked. A label, not a warning, so it
                        shows whatever the Studio toggle says — as the picker's does. */}
                    {isExternal(p.email, ownDomains) && <span className={styles.acExternal}>External</span>}
                    <button
                      type="button"
                      onClick={() => setAttendees((list) => list.filter((a) => a.email !== p.email))}
                      aria-label={`Remove ${p.name}`}
                    >
                      <CloseSmIcon />
                    </button>
                  </span>
                );
              })}
            </div>
          )}
          <AttendeeInput
            when={presetRange}
            token={token}
            selfEmails={self.emails}
            exclude={new Set([...self.emails, ...emails])}
            loadSchedules={loadSchedules}
            onPick={(person) => setAttendees((list) => [...list, person])}
            placeholder="Add someone — type a name"
            onBackspaceEmpty={() => setAttendees((list) => list.slice(0, -1))}
          />
        </div>

        <div>
          <div className={styles.fLbl}>Subject</div>
          <input
            className={styles.inp}
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="What's it about?"
            aria-label="Subject"
          />
        </div>

        <div>
          <div className={styles.fLbl}>Duration</div>
          <div className={styles.chips}>
            {DURATIONS.map((d) => (
              <button
                key={d}
                type="button"
                className={`${styles.chip} ${duration === d ? styles.chipOn : ""}`}
                aria-pressed={duration === d}
                onClick={() => setDuration(d)}
              >
                {fd(d)}
              </button>
            ))}
          </div>
        </div>

        <div>
          <div className={styles.fLbl}>Location</div>
          <div className={styles.chips}>
            {PLACES.map((p) => (
              <button
                key={p.value}
                type="button"
                className={`${styles.chip} ${place === p.value ? styles.chipOn : ""}`}
                aria-pressed={place === p.value}
                onClick={() => setPlace(p.value)}
              >
                {p.label}
              </button>
            ))}
          </div>
          {place === "inperson" && (
            <input
              className={`${styles.inp} ${styles.inpBelow}`}
              value={where}
              onChange={(e) => setWhere(e.target.value)}
              placeholder="Where? e.g. a room or an address"
              aria-label="Where"
            />
          )}
          {place === "teams" && <span className={styles.hint}>A Teams meeting gets its join link automatically.</span>}
        </div>

        <RepeatField repeat={repeat} start={presetRange?.start ?? null} onChange={setRepeat} />

        {externalWarning}

        {when}
      </div>
      {toast}
    </div>
  );
}

interface PresetWhenProps {
  readonly start: Date;
  readonly end: Date;
  /** The week on screen, for whether you're free then. */
  readonly events: readonly CalEvent[];
  readonly emails: readonly string[];
  readonly statusAt: (email: string) => Availability;
  readonly firstName: (email: string) => string;
  /** Colleagues' availability is still loading. */
  readonly checking: boolean;
  readonly bookDisabled: boolean;
  readonly bookLabel: (createLabel: string) => string;
  /** Under the buttons: what booking does (opens Outlook, or AiVA sends it). */
  readonly bookHint: string;
  readonly onBook: (start: Date, end: Date) => void;
  readonly onChange: () => void;
  readonly onFindBetter: () => void;
}

/** Step 1's When card for the time clicked in the day view: who's busy then, and booking it. */
function PresetWhen({
  start,
  end,
  events,
  emails,
  statusAt,
  firstName,
  checking,
  bookDisabled,
  bookLabel,
  bookHint,
  onBook,
  onChange,
  onFindBetter,
}: Readonly<PresetWhenProps>) {
  const meBusy = events.some((e) => !e.isAllDay && isActive(e) && e.start < end && start < e.end);
  const busyNames = [...(meBusy ? ["You"] : []), ...emails.filter((e) => statusAt(e) === "busy").map(firstName)];
  const unknownCount = emails.filter((e) => statusAt(e) === "unknown").length;
  const ok = busyNames.length === 0;
  let line: string;
  if (!ok) line = `${busyNames.join(", ")} ${busyNames.length === 1 && !meBusy ? "is" : "are"} busy`;
  else if (emails.length > 0 && checking) line = "Checking availability…";
  else if (emails.length === 0) line = "You're free";
  else if (unknownCount > 0) line = `You're free · availability unknown for ${unknownCount} of ${emails.length}`;
  else line = "Everyone is free";
  return (
    <>
      <div>
        <div className={styles.fLbl}>When</div>
        <div className={styles.when}>
          <span className={ok ? styles.whenOk : styles.whenWarn}>{ok ? <CheckIcon /> : <WarnIcon />}</span>
          <span className={styles.whenText}>
            {dayLabel(dayKeyOf(start))} · {rangeLabel(start, end)}
            <span className={styles.whenSub}>{line}</span>
          </span>
          <button type="button" className={styles.newLink} onClick={onChange}>
            Change
          </button>
        </div>
      </div>
      <div className={styles.rowBtns}>
        <button type="button" className={styles.btn} disabled={bookDisabled} onClick={() => onBook(start, end)}>
          {bookLabel(ok ? `Book at ${hm(minutesOfDay(start))}` : "Book Anyway")}
        </button>
        <button type="button" className={`${styles.btn} ${styles.btnSecondary}`} onClick={onFindBetter}>
          Find a Better Time
        </button>
      </div>
      <span className={styles.hint}>{bookHint}</span>
    </>
  );
}

/** The Repeat chips: weekly or fortnightly on the meeting's weekday, with no end date. */
function RepeatField({ repeat, start, onChange }: Readonly<{ repeat: Repeat; start: Date | null; onChange: (r: Repeat) => void }>) {
  const every = repeat === "weekly" ? "Weekly" : "Every 2 weeks";
  const on = start ? ` on ${WEEKDAY_NAMES[start.getDay()] ?? ""}s` : "";
  return (
    <div>
      <div className={styles.fLbl}>Repeat</div>
      <div className={styles.chips}>
        {REPEATS.map((r) => (
          <button
            key={r.value}
            type="button"
            className={`${styles.chip} ${repeat === r.value ? styles.chipOn : ""}`}
            aria-pressed={repeat === r.value}
            onClick={() => onChange(r.value)}
          >
            {r.label}
          </button>
        ))}
      </div>
      {repeat !== "none" && (
        <span className={styles.hint}>{`${every}${on}, with no end date. You can end the series in Outlook at any time.`}</span>
      )}
    </div>
  );
}

/**
 * Your own email domains, and the attendees outside them to name before an invite goes. "Outside" is the attendee
 * picker's own rule (not on any of your addresses' domains), so the warning, the picker's External label and the
 * chips' External tag always agree. No one is named while the Studio "External recipient warning" is switched off.
 */
function useOutsiders(attendees: readonly Person[], self: Self) {
  const warnExternal = useOptionalAppConfig()?.data?.externalRecipientWarnEnabled ?? true;
  const ownDomains = useMemo(() => new Set([...self.emails].map(domainOf).filter(Boolean)), [self.emails]);
  const outsiders = warnExternal ? attendees.filter((a) => isExternal(a.email, ownDomains)) : [];
  return { ownDomains, outsiders };
}

/** The yellow bar naming the people outside the organisation an invite goes to; nothing when there are none. */
function ExternalInviteWarning({ outsiders }: Readonly<{ outsiders: readonly Person[] }>) {
  if (outsiders.length === 0) return null;
  const who = outsiders.length === 1 ? "1 person" : `${outsiders.length} people`;
  return (
    <MessageBar intent="warning" layout="multiline" className={styles.externalWarning}>
      <MessageBarBody className={styles.externalWarningBody}>
        This invite goes to {who} outside your organisation: {outsiders.map((a) => a.email).join(", ")}.
      </MessageBarBody>
    </MessageBar>
  );
}
